import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../LanguageContext";
import { optImg } from "../lib/img";

const Hero = () => {
  const { t, images } = useLang();
  const IMAGES = images;
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 220]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  const titleLines = t.hero.title.split("\n");

  return (
    <section className="relative h-screen w-full overflow-hidden bg-ink grain">
      {/* Background */}
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={optImg(IMAGES.hero, 1920, 72)} alt="" fetchpriority="high" decoding="async" className="h-[115%] w-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink/85" />
        <div className="absolute inset-0 bg-ink/20" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}
          className="flex items-center gap-3 mb-7">
          <span className="h-px w-10 bg-gold" />
          <span className="overline text-gold-light">{t.hero.overline}</span>
        </motion.div>

        <h1 className="font-serif text-white font-light leading-[0.95] text-[15vw] md:text-[9.5vw] lg:text-[8.5rem] max-w-5xl">
          {titleLines.map((line, i) => (
            <span key={i} className="reveal-line block">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.5 + i * 0.15, ease: [0.19, 1, 0.22, 1] }}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }}
          className="mt-8 max-w-xl text-white/80 text-base md:text-lg font-light leading-relaxed">
          {t.hero.subtitle}
        </motion.p>

        <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }}
          onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
          className="btn-line group mt-11 w-fit border border-white/40 text-white px-9 py-4 text-[12px] uppercase tracking-[0.22em]">
          <span className="group-hover:text-white transition-colors duration-300">{t.cta.discover}</span>
        </motion.button>
      </motion.div>

      {/* Scroll cue */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-white/60">
        <span className="overline text-[0.6rem]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.2} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
